'use client';

import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { NestedSheetStackProvider } from '@/shared/ui';
import { useOptionalDesktopLayout } from '@/features/project/context/DesktopLayoutContext';

interface AngleTab {
  id: string;
  name: string;
  isMain: boolean;
}

interface InlineContainerProps {
  children: ReactNode;
  // Album content dispatched when the Design/Album picker (in the
  // content column — see `ConceptContentTabs`) selects "album".
  albumContent?: ReactNode;
  // Estimate settings (EstimateSettingsPanel) dispatched when the picker
  // selects "estimate" — the estimate document lives in the content column.
  estimateContent?: ReactNode;
  // Default-angle bootstrapping: kept here because the inspector is
  // what actually reads `selectedAngleId` to render settings for the
  // current angle, and on cold-mount we want main angle selected.
  angleTabs?: AngleTab[];
  selectedAngleId?: string;
  onAngleChange?: (angleId: string | undefined) => void;
  mainAngleId?: string;
  /** Stacked (single-column) layout: render in normal page flow instead of
   *  filling a fixed-height inspector column. */
  isStacked?: boolean;
}

/**
 * Inline (two-column) settings panel container.
 *
 * The Design/Album picker and angle tabs UI live in the content column
 * via [`ConceptContentTabs`] — see Apple HIG split-view convention:
 * navigation goes with the content, inspector holds only tools.
 *
 * This container now just dispatches between settings and album content
 * based on `layoutContext.activeTab`, and keeps the cold-start
 * "default to main angle" behavior.
 */
export function InlineContainer({
  children,
  albumContent,
  estimateContent,
  angleTabs = [],
  selectedAngleId,
  onAngleChange,
  mainAngleId,
  isStacked = false,
}: InlineContainerProps) {
  const layoutContext = useOptionalDesktopLayout();

  const topTab = layoutContext?.activeTab ?? 'design';
  const contextSelectedAngleId = layoutContext?.selectedAngleId;
  const setContextSelectedAngleId =
    layoutContext?.setSelectedAngleId ?? (() => {});

  const currentAngleId = contextSelectedAngleId ?? selectedAngleId;
  const handleAngleChange = (id: string | undefined) => {
    setContextSelectedAngleId(id);
    onAngleChange?.(id);
  };

  // Cold-mount: default to main angle when nothing's selected yet.
  useEffect(() => {
    if (!currentAngleId && mainAngleId) {
      handleAngleChange(undefined);
    }
  }, [mainAngleId, currentAngleId]);

  // angleTabs is retained in props so callers can keep passing it
  // without churn; the inspector no longer renders the tab UI itself.
  void angleTabs;

  return (
    <NestedSheetStackProvider isStacked={isStacked}>
      {/* Inspector column has no chrome — no fill, no border, no shadow.
          Groups (`SettingsCard` → `bg-settings-card`) float on the page
          panel-gray inherited from the parent. Two-column fills the fixed
          inspector height (`h-full`); stacked flows in the page scroll. */}
      <div className={isStacked ? 'flex flex-col' : 'h-full flex flex-col'}>
        {topTab === 'design' && children}
        {topTab === 'album' && albumContent}
        {/* Estimate tab: the inspector hosts the estimate SETTINGS +
            Regenerate CTA (EstimateSettingsPanel) while the estimate document
            renders in the wide content column (ConceptEditor). The panel
            renders nothing for the intro/generating/failed states. */}
        {topTab === 'estimate' && estimateContent}
      </div>
    </NestedSheetStackProvider>
  );
}
